import { useEffect, useRef, useState } from 'react'
import type { FormEvent } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { vehiclesApi } from '@/api/endpoints'
import type { FuelType, Vehicle, VehicleCategory, VehicleFileKind, VehicleOwnership, VehicleRequest } from '@/api/types'
import { Button, Card, Field, Input, Select, Textarea } from '@/components/ui'
import { errorMessage } from '@/utils/errors'
import { FUEL_LABELS, OWNERSHIP_LABELS, formatBytes, formatDate, formatDateTime, formatNumber, formatPlate } from '@/utils/format'

interface Form {
  plate: string
  name: string
  vin: string
  category: VehicleCategory
  fuelType: FuelType
  consumptionCity: string
  consumptionHighway: string
  consumptionCombined: string
  ownership: VehicleOwnership
  ownerName: string
  initialOdometerKm: string
  inServiceFrom: string
  active: boolean
  note: string
}

const EMPTY: Form = {
  plate: '',
  name: '',
  vin: '',
  category: 'CAR',
  fuelType: 'PETROL_95',
  consumptionCity: '',
  consumptionHighway: '',
  consumptionCombined: '',
  ownership: 'COMPANY',
  ownerName: '',
  initialOdometerKm: '',
  inServiceFrom: '',
  active: true,
  note: '',
}

const num = (n: number | null) => (n === null ? '' : String(n).replace('.', ','))
/** "5,4" i "5.4" → 5.4; prázdné → null. */
const parse = (s: string) => (s.trim() === '' ? null : Number(s.replace(/\s/g, '').replace(',', '.')))

function toForm(v: Vehicle): Form {
  return {
    plate: formatPlate(v.plate),
    name: v.name,
    vin: v.vin ?? '',
    category: v.category,
    fuelType: v.fuelType,
    consumptionCity: num(v.consumptionCity),
    consumptionHighway: num(v.consumptionHighway),
    consumptionCombined: num(v.consumptionCombined),
    ownership: v.ownership,
    ownerName: v.ownerName ?? '',
    initialOdometerKm: v.initialOdometerKm === null ? '' : String(v.initialOdometerKm),
    inServiceFrom: v.inServiceFrom ?? '',
    active: v.active,
    note: v.note ?? '',
  }
}

function toRequest(f: Form): VehicleRequest {
  return {
    plate: f.plate,
    name: f.name,
    vin: f.vin || null,
    category: f.category,
    fuelType: f.fuelType,
    consumptionCity: parse(f.consumptionCity),
    consumptionHighway: parse(f.consumptionHighway),
    consumptionCombined: parse(f.consumptionCombined),
    ownership: f.ownership,
    ownerName: f.ownership === 'EMPLOYEE' ? f.ownerName : null,
    initialOdometerKm: parse(f.initialOdometerKm),
    inServiceFrom: f.inServiceFrom || null,
    active: f.active,
    note: f.note || null,
  }
}

/** Detail auta i formulář pro nové (`/vozidla/nove`). */
export default function VehicleDetailPage() {
  const { id } = useParams<{ id: string }>()
  const isNew = id === 'nove'
  const navigate = useNavigate()
  const [vehicle, setVehicle] = useState<Vehicle | null>(null)
  const [form, setForm] = useState<Form>(EMPTY)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [message, setMessage] = useState<string | null>(null)
  const [confirmDelete, setConfirmDelete] = useState(false)

  const apply = (v: Vehicle) => {
    setVehicle(v)
    setForm(toForm(v))
  }

  useEffect(() => {
    if (isNew || !id) return
    vehiclesApi
      .detail(id)
      .then(apply)
      .catch((e) => setError(errorMessage(e, 'Auto se nepodařilo načíst.')))
  }, [id, isNew])

  const set = <K extends keyof Form>(key: K, value: Form[K]) => setForm((f) => ({ ...f, [key]: value }))

  const save = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    setMessage(null)
    const numeric = [form.consumptionCombined, form.consumptionCity, form.consumptionHighway, form.initialOdometerKm]
    if (numeric.some((v) => Number.isNaN(parse(v)))) {
      setError('Spotřeba a stav tachometru musí být čísla.')
      return
    }
    setBusy(true)
    try {
      if (isNew) {
        const created = await vehiclesApi.create(toRequest(form))
        navigate(`/vozidla/${created.id}`, { replace: true })
      } else {
        apply(await vehiclesApi.update(id!, toRequest(form)))
        setMessage('Uloženo.')
      }
    } catch (err) {
      setError(errorMessage(err, 'Auto se nepodařilo uložit.'))
    } finally {
      setBusy(false)
    }
  }

  const remove = async () => {
    if (!confirmDelete) return setConfirmDelete(true)
    try {
      await vehiclesApi.remove(id!)
      navigate('/vozidla')
    } catch (err) {
      setError(errorMessage(err, 'Auto se nepodařilo smazat.'))
    }
  }

  if (!isNew && !vehicle) {
    return error ? <p className="text-sm text-red-600">{error}</p> : <p className="text-slate-400">Načítání…</p>
  }

  const unit = form.fuelType === 'ELECTRIC' ? 'kWh' : form.fuelType === 'CNG' ? 'kg' : 'l'

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <Link to="/vozidla" className="text-sm text-slate-500 hover:text-slate-700">
          ← Vozový park
        </Link>
        <h1 className="text-lg font-semibold">
          {isNew ? 'Nové auto' : `${formatPlate(vehicle!.plate)} · ${vehicle!.name}`}
        </h1>
      </div>

      <div className="grid gap-4 lg:grid-cols-[3fr_2fr]">
        <Card>
          <form onSubmit={save} className="space-y-4">
            <div className="grid gap-3 sm:grid-cols-[1fr_2fr]">
              <Field label="SPZ">
                <Input required value={form.plate} onChange={(e) => set('plate', e.target.value)} placeholder="1AB 2345" />
              </Field>
              <Field label="Auto">
                <Input
                  required
                  value={form.name}
                  onChange={(e) => set('name', e.target.value)}
                  placeholder="Škoda Octavia Combi 2.0 TDI"
                />
              </Field>
            </div>
            <div className="grid gap-3 sm:grid-cols-3">
              <Field label="VIN">
                <Input value={form.vin} onChange={(e) => set('vin', e.target.value)} className="font-mono" />
              </Field>
              <Field label="Druh">
                <Select value={form.category} onChange={(e) => set('category', e.target.value as VehicleCategory)}>
                  <option value="CAR">Osobní auto</option>
                  <option value="MOTORCYCLE">Motocykl / tříkolka</option>
                </Select>
              </Field>
              <Field label="Palivo">
                <Select value={form.fuelType} onChange={(e) => set('fuelType', e.target.value as FuelType)}>
                  {(Object.keys(FUEL_LABELS) as FuelType[]).map((f) => (
                    <option key={f} value={f}>
                      {FUEL_LABELS[f]}
                    </option>
                  ))}
                </Select>
              </Field>
            </div>

            <fieldset className="rounded-lg border border-slate-200 p-3">
              <legend className="px-1 text-xs font-medium uppercase tracking-wide text-slate-500">
                Spotřeba z technického průkazu ({unit}/100 km)
              </legend>
              <div className="grid grid-cols-3 gap-3">
                <Field label="Kombinovaná">
                  <Input inputMode="decimal" value={form.consumptionCombined} onChange={(e) => set('consumptionCombined', e.target.value)} />
                </Field>
                <Field label="Město">
                  <Input inputMode="decimal" value={form.consumptionCity} onChange={(e) => set('consumptionCity', e.target.value)} />
                </Field>
                <Field label="Mimo město">
                  <Input inputMode="decimal" value={form.consumptionHighway} onChange={(e) => set('consumptionHighway', e.target.value)} />
                </Field>
              </div>
              <p className="mt-2 text-xs text-slate-400">
                Pro náhrady se bere kombinovaná; když ji TP neuvádí, průměr ostatních údajů.
              </p>
            </fieldset>

            <fieldset className="rounded-lg border border-slate-200 p-3">
              <legend className="px-1 text-xs font-medium uppercase tracking-wide text-slate-500">Komu auto patří</legend>
              <div className="flex flex-wrap gap-4 text-sm">
                {(Object.keys(OWNERSHIP_LABELS) as VehicleOwnership[]).map((o) => (
                  <label key={o} className="flex items-center gap-2">
                    <input type="radio" checked={form.ownership === o} onChange={() => set('ownership', o)} />
                    {OWNERSHIP_LABELS[o]}
                  </label>
                ))}
              </div>
              {form.ownership === 'EMPLOYEE' && (
                <div className="mt-3">
                  <Field label="Zaměstnanec (vlastník)">
                    <Input required value={form.ownerName} onChange={(e) => set('ownerName', e.target.value)} placeholder="Jméno Příjmení" />
                  </Field>
                </div>
              )}
              <p className="mt-2 text-xs text-slate-400">
                {form.ownership === 'EMPLOYEE'
                  ? 'Za služební jízdy se zaměstnanci platí základní náhrada za km (amortizace) a náhrada za PHM.'
                  : 'Firemní auto se odpisuje ve firmě — zaměstnanci se platí nanejvýš PHM, které sám zaplatil.'}
              </p>
            </fieldset>

            <div className="grid gap-3 sm:grid-cols-3">
              <Field label="V evidenci od">
                <Input type="date" value={form.inServiceFrom} onChange={(e) => set('inServiceFrom', e.target.value)} />
              </Field>
              <Field label="Stav tachometru (km)">
                <Input inputMode="numeric" value={form.initialOdometerKm} onChange={(e) => set('initialOdometerKm', e.target.value)} />
              </Field>
              <label className="flex items-end gap-2 pb-2 text-sm">
                <input type="checkbox" checked={form.active} onChange={() => set('active', !form.active)} />
                Používá se
              </label>
            </div>

            <Field label="Poznámka">
              <Textarea rows={2} value={form.note} onChange={(e) => set('note', e.target.value)} />
            </Field>

            <div className="flex flex-wrap items-center gap-2 border-t border-slate-100 pt-4">
              <Button type="submit" disabled={busy}>
                {isNew ? 'Přidat auto' : 'Uložit'}
              </Button>
              {!isNew && (
                <Button
                  type="button"
                  variant={confirmDelete ? 'danger' : 'ghost'}
                  onClick={remove}
                  onBlur={() => setConfirmDelete(false)}
                >
                  {confirmDelete ? 'Opravdu smazat i s přílohami?' : 'Smazat'}
                </Button>
              )}
              {vehicle?.updatedByEmail && (
                <span className="text-xs text-slate-400">
                  Naposledy {vehicle.updatedByEmail}, {formatDateTime(vehicle.updatedAt)}
                </span>
              )}
            </div>
            {message && <p className="text-sm text-green-700">{message}</p>}
            {error && <p className="text-sm text-red-600">{error}</p>}
          </form>
        </Card>

        {vehicle && (
          <div className="space-y-4">
            <Card className="text-sm">
              <div className="flex items-center justify-between">
                <h2 className="font-semibold">Kniha jízd</h2>
                <Link to={`/cesty?auto=${vehicle.id}`} className="text-brand-600 hover:underline">
                  jízdy →
                </Link>
              </div>
              <dl className="mt-3 space-y-1">
                <Row label="Najeto v knize jízd" value={`${vehicle.drivenKm.toLocaleString('cs-CZ')} km`} />
                <Row
                  label="Stav tachometru"
                  value={
                    vehicle.currentOdometerKm !== null
                      ? `${vehicle.currentOdometerKm.toLocaleString('cs-CZ')} km`
                      : 'vyplň počáteční stav'
                  }
                />
              </dl>
            </Card>
            <RateCard vehicle={vehicle} />
            <FilesCard vehicle={vehicle} onChange={apply} />
          </div>
        )}
      </div>
    </div>
  )
}

function RateCard({ vehicle: v }: { vehicle: Vehicle }) {
  const r = v.rateToday
  return (
    <Card className="text-sm">
      <h2 className="mb-3 font-semibold">Náhrada za 1 km k {formatDate(r.date)}</h2>
      <dl className="space-y-1">
        {v.ownership === 'EMPLOYEE' && (
          <Row label="Základní náhrada" value={r.base ? `${formatNumber(r.base.value)} Kč` : '—'} hint={r.base?.source} />
        )}
        <Row
          label="Spotřeba"
          value={r.consumption !== null ? `${formatNumber(r.consumption)} ${v.fuelUnit}/100 km` : 'doplň z TP'}
        />
        <Row
          label="Cena PHM"
          value={r.fuelPrice ? `${formatNumber(r.fuelPrice.value)} Kč/${v.fuelUnit}` : 'z dokladu o nákupu'}
          hint={r.fuelPrice?.source}
        />
        <Row label="PHM na 1 km" value={r.fuel !== null ? `${formatNumber(r.fuel)} Kč` : '—'} />
        <div className="flex justify-between border-t border-slate-100 pt-2 font-semibold">
          <dt>Celkem za 1 km</dt>
          <dd>{r.total !== null ? `${formatNumber(r.total)} Kč` : '—'}</dd>
        </div>
      </dl>
      {v.ownership === 'COMPANY' && (
        <p className="mt-2 text-xs text-slate-400">Platí se jen, když zaměstnanec PHM do firemního auta zaplatil sám.</p>
      )}
    </Card>
  )
}

function Row({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="flex justify-between gap-3">
      <dt className="text-slate-500">{label}</dt>
      <dd className="text-right">
        {value}
        {hint && <span className="ml-1 text-xs text-slate-400">({hint})</span>}
      </dd>
    </div>
  )
}

const KIND_LABELS: Record<VehicleFileKind, string> = {
  TECHNICAL_CERTIFICATE: 'Technický průkaz',
  OTHER: 'Další přílohy',
}

function FilesCard({ vehicle, onChange }: { vehicle: Vehicle; onChange: (v: Vehicle) => void }) {
  const [uploading, setUploading] = useState<VehicleFileKind | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null)
  const inputs = useRef<Partial<Record<VehicleFileKind, HTMLInputElement | null>>>({})

  const upload = async (kind: VehicleFileKind, files: File[]) => {
    if (files.length === 0) return
    setUploading(kind)
    setError(null)
    try {
      onChange(await vehiclesApi.upload(vehicle.id, kind, files))
    } catch (e) {
      setError(errorMessage(e, 'Soubor se nepodařilo nahrát.'))
    } finally {
      setUploading(null)
    }
  }

  const removeFile = async (fileId: string) => {
    if (confirmDelete !== fileId) return setConfirmDelete(fileId)
    try {
      onChange(await vehiclesApi.removeFile(fileId))
    } catch (e) {
      setError(errorMessage(e, 'Přílohu se nepodařilo smazat.'))
    }
  }

  return (
    <Card>
      <h2 className="mb-3 font-semibold">Přílohy</h2>
      {(['TECHNICAL_CERTIFICATE', 'OTHER'] as VehicleFileKind[]).map((kind) => {
        const list = vehicle.files.filter((f) => f.kind === kind)
        return (
          <div key={kind} className="mb-4 last:mb-0">
            <div className="mb-1 flex items-center justify-between">
              <h3 className="text-xs font-medium uppercase tracking-wide text-slate-500">{KIND_LABELS[kind]}</h3>
              <button
                type="button"
                className="text-xs text-brand-600 hover:underline"
                onClick={() => inputs.current[kind]?.click()}
                disabled={uploading !== null}
              >
                {uploading === kind ? 'nahrávám…' : '+ nahrát'}
              </button>
              <input
                ref={(el) => {
                  inputs.current[kind] = el
                }}
                type="file"
                multiple
                accept={kind === 'TECHNICAL_CERTIFICATE' ? 'application/pdf,image/*' : undefined}
                className="hidden"
                onChange={(e) => {
                  upload(kind, Array.from(e.target.files ?? []))
                  e.target.value = ''
                }}
              />
            </div>
            {list.length === 0 ? (
              <p className="text-sm text-slate-400">
                {kind === 'TECHNICAL_CERTIFICATE' ? 'Nahraj PDF výpisu z TP — je z něj spotřeba.' : 'Nic.'}
              </p>
            ) : (
              <ul className="divide-y divide-slate-100">
                {list.map((f) => (
                  <li key={f.id} className="py-2 text-sm">
                    <a
                      href={vehiclesApi.fileUrl(f.id, true)}
                      target="_blank"
                      rel="noreferrer"
                      className="block truncate font-medium text-brand-700 hover:underline"
                    >
                      {f.fileName}
                    </a>
                    <div className="mt-0.5 flex items-center gap-3 text-xs text-slate-400">
                      <span>
                        {formatBytes(f.sizeBytes)} · {formatDateTime(f.createdAt)}
                      </span>
                      <span className="ml-auto flex gap-3">
                        <a href={vehiclesApi.fileUrl(f.id)} className="text-brand-600 hover:underline">
                          stáhnout
                        </a>
                        <button
                          onClick={() => removeFile(f.id)}
                          onBlur={() => setConfirmDelete(null)}
                          className={`hover:underline ${confirmDelete === f.id ? 'font-semibold text-red-700' : 'text-red-500'}`}
                        >
                          {confirmDelete === f.id ? 'opravdu smazat?' : 'smazat'}
                        </button>
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )
      })}
      {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
    </Card>
  )
}
